import React, { ReactNode } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import { StyledAlert, ClickableText } from './styles';
import { useCurrentUser } from 'libs/hooks';
import { reportError } from '@xbcb/ui-utils';
import { useHistory } from 'react-router-dom';
import { useTypedSelector, RootState } from 'reducers';
// Adjust import path as needed
import {
  DateSearchOperator,
  GetLatestViewedReleaseNoteByUserQuery,
  GetLatestViewedReleaseNoteByUserDocument,
  SearchReleaseNotesQuery,
  SearchReleaseNotesDocument,
  GetLatestReleaseNoteQuery,
  GetLatestReleaseNoteDocument,
  CreateUserReleaseNoteViewDocument,
  CreateUserReleaseNoteViewMutation,
} from '__generated__/graphql';
import { getCountryAbbreviationFromCustomsBrokerId } from '@xbcb/core';
import { SupportedCountryAbbreviation } from '@xbcb/shared-types';

export const ReleaseNoteBanner: React.FC = () => {
  const { loading: userLoading, ...currentUser } = useCurrentUser();
  const operatorUserId = currentUser?.id;
  const history = useHistory();

  const currentActiveBrokerId = useTypedSelector(
    (state: RootState) => state.activeBroker.currentActiveBroker,
  );

  const countryOfOperation = currentActiveBrokerId
    ? getCountryAbbreviationFromCustomsBrokerId(currentActiveBrokerId)
    : undefined;

  const { data: latestViewData, refetch: refetchLatestView } =
    useQuery<GetLatestViewedReleaseNoteByUserQuery>(
      GetLatestViewedReleaseNoteByUserDocument,
      {
        variables: {
          userId: operatorUserId,
        },
        skip: userLoading || !operatorUserId,
        onError: (error) => {
          reportError('Get latest viewed release note error');
        },
      },
    );

  const lastViewedTime =
    latestViewData?.getLatestViewedReleaseNoteByUser?.created?.time;

  const { data: releaseNotesData, refetch: refetchReleaseNotes } =
    useQuery<SearchReleaseNotesQuery>(SearchReleaseNotesDocument, {
      variables: {
        input: {
          searchCriteria: {
            createdTime: {
              operator: DateSearchOperator.AFTER,
              value: lastViewedTime || new Date(0).toISOString(),
            },
            deletedTime: {
              operator: DateSearchOperator.DOES_NOT_EXIST,
            },
          },
        },
      },
      skip: userLoading || !operatorUserId,
      onError: (error) => {
        reportError('Search release notes error');
      },
    });

  const { data: latestReleaseNoteData } = useQuery<GetLatestReleaseNoteQuery>(
    GetLatestReleaseNoteDocument,
    {
      skip:
        !releaseNotesData ||
        releaseNotesData.searchReleaseNotes?.totalResults !== 1,
      onError: (error) => {
        reportError('Get latest release note error');
      },
    },
  );

  const [createUserReleaseNoteView] =
    useMutation<CreateUserReleaseNoteViewMutation>(
      CreateUserReleaseNoteViewDocument,
    );

  const handleNavigateToReleaseNotes = () => {
    history.push('/release-notes');
  };

  const handleClose = async () => {
    if (!operatorUserId) return;

    try {
      if (
        releaseNotesData?.searchReleaseNotes.totalResults === 1 &&
        latestReleaseNoteData?.getLatestReleaseNote
      ) {
        await createUserReleaseNoteView({
          variables: {
            input: {
              user: { id: operatorUserId },
              releaseNote: {
                id: latestReleaseNoteData.getLatestReleaseNote.id,
              },
            },
            idempotencyKey: uuidv4(),
          },
        });
      } else if (releaseNotesData?.searchReleaseNotes.results) {
        await Promise.all(
          releaseNotesData.searchReleaseNotes.results.map((note) =>
            createUserReleaseNoteView({
              variables: {
                input: {
                  user: { id: operatorUserId },
                  releaseNote: { id: note.id },
                },
                idempotencyKey: uuidv4(),
              },
            }),
          ),
        );
      }

      await Promise.all([refetchLatestView(), refetchReleaseNotes()]);
    } catch (error) {
      reportError('Failed to mark release note(s) as viewed');
    }
  };

  // Early returns
  if (
    userLoading ||
    !operatorUserId ||
    !releaseNotesData?.searchReleaseNotes?.results ||
    releaseNotesData.searchReleaseNotes.totalResults === 0 ||
    countryOfOperation !== SupportedCountryAbbreviation.US
  ) {
    return null;
  }

  const totalUnviewed = releaseNotesData.searchReleaseNotes.totalResults;

  // Determine banner message based on conditions
  let bannerMessage: ReactNode;
  if (totalUnviewed === 1 && latestReleaseNoteData?.getLatestReleaseNote) {
    const latestNote = latestReleaseNoteData.getLatestReleaseNote;
    bannerMessage = (
      <div>
        <ClickableText onClick={handleNavigateToReleaseNotes}>
          <span className="title">{latestNote.title}</span>
        </ClickableText>
        {latestNote.description || ''}
      </div>
    );
  } else {
    bannerMessage = (
      <span>
        You have {totalUnviewed} unviewed releases. Please go to the{' '}
        <ClickableText onClick={handleNavigateToReleaseNotes}>
          release notes page
        </ClickableText>{' '}
        to view them!
      </span>
    );
  }

  return (
    <StyledAlert
      showIcon
      type="info"
      message={bannerMessage}
      closable
      onClose={handleClose}
    />
  );
};

export default ReleaseNoteBanner;
